import moment from 'moment';
import { i18n } from '@/plugins/i18n';
import { NotificationSlugs } from '@/util/constants/notificationSlugs';
import { formatNumToCurrency } from '@/util/currency-util';
import { SHORT_DATE_FORMAT } from '@/util/date-util';
import { normalizeStr } from '@/util/string-util';

export const CHANGE_KINDS = {
  ADDED: 'ADDED',
  REMOVED: 'REMOVED',
  UPDATED: 'UPDATED',
  REPLACED: 'REPLACED',
};

/** Typed list fields whose per-item diffs arrive pre-computed in `valuesChange`. */
export const TYPED_LIST_FIELDS = ['documents', 'interestedTenderers', 'awards', 'bids', 'lots', 'classificationsList'];

const MONEY_FIELDS = ['value', 'minValue'];
const DATE_FIELDS = ['publishedDate', 'startDate', 'endDate'];
const DESCRIPTION_MAX_LENGTH = 200;
const CLASSIFICATION_DESCRIPTION_MAX_LENGTH = 100;

const truncate = (text, maxLength) => (text && text.length > maxLength ? `${text.slice(0, maxLength)}...` : text);

/** Fields rendered as per-item rows (typed lists + the string list `coveredBy`). */
export const isRowChange = (change) => TYPED_LIST_FIELDS.includes(change.field) || change.field === 'coveredBy';

/** Changes worth rendering: whitelisted by the server, and producing at least one row when row-based. */
export const filterRenderableTenderChanges = (tenderChanges) =>
  (tenderChanges || []).filter(
    (change) => change.fieldForMarketSummary && (!isRowChange(change) || getChangeRows(change).length > 0),
  );

/** Translated card title; the field labels already include "modifié(e)(s)". */
export const getChangeTitle = (change) => {
  const key = `notifications-center.field.${change.field}`;
  return i18n.te(key) ? i18n.t(key) : change.field;
};

const getItemAmount = (item) => (item.value && item.value.amount != null ? item.value.amount : null);

const formatClassification = (item) =>
  [item.code, truncate(item.description, CLASSIFICATION_DESCRIPTION_MAX_LENGTH)].filter(Boolean).join(' - ') +
  (item.scheme ? ` (${item.scheme})` : '');

const toRow = (kind, label, amount = null) => ({
  kind: kind === CHANGE_KINDS.ADDED || kind === CHANGE_KINDS.REMOVED ? kind : null,
  label,
  amount,
});

const getTypedListRows = (change) =>
  (change.valuesChange || []).flatMap(({ value, kind }) => {
    const item = value || {};
    switch (change.field) {
      case 'documents':
        return [toRow(kind, item.title)];
      case 'interestedTenderers':
        return [toRow(kind, item.organization && item.organization.name)];
      case 'bids':
        return [toRow(kind, item.tenderer && item.tenderer.name, getItemAmount(item))];
      case 'awards':
        return (item.suppliers || []).map((supplier) => toRow(kind, supplier.name, getItemAmount(item)));
      case 'lots':
        return [toRow(kind, item.title, getItemAmount(item))];
      case 'classificationsList':
        return [toRow(kind, formatClassification(item))];
      default:
        return [];
    }
  });

/** `coveredBy` has no `valuesChange` — removed/added come from `children`, the rest of `newValue` is unchanged. */
const getCoveredByRows = (change) => {
  const children = change.children || [];
  const removed = children.filter((child) => child.kind === CHANGE_KINDS.REMOVED).map((child) => child.oldValue);
  const added = children.filter((child) => child.kind === CHANGE_KINDS.ADDED).map((child) => child.newValue);
  const unchanged = (change.newValue || []).filter((item) => !added.includes(item));

  return [
    ...removed.map((item) => toRow(CHANGE_KINDS.REMOVED, item)),
    ...added.map((item) => toRow(CHANGE_KINDS.ADDED, item)),
    ...unchanged.map((item) => toRow(null, item)),
  ];
};

/**
 * Per-item rows of a row-based change: `{kind: ADDED|REMOVED|null, label, amount}`
 * (null kind = unchanged). `valuesChange` arrives pre-ordered removed → added → unchanged.
 */
export const getChangeRows = (change) => {
  const rows = change.field === 'coveredBy' ? getCoveredByRows(change) : getTypedListRows(change);
  return rows.filter((row) => row.label);
};

/** Lowest amount among a bids change's non-removed rows; ties all count as lowest (null when no amounts). */
export const getLowestBidAmount = (rows) => {
  const amounts = (rows || [])
    .filter((row) => row.kind !== CHANGE_KINDS.REMOVED)
    .map((row) => row.amount)
    .filter((amount) => amount !== null);
  return amounts.length > 0 ? Math.min(...amounts) : null;
};

const formatDateValue = (value) => {
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format(SHORT_DATE_FORMAT) : String(value);
};

const formatTenderPeriod = (value) => {
  if (!value.startDate && !value.endDate) return null;
  return [value.startDate, value.endDate]
    .map((date) => (date ? formatDateValue(date) : i18n.t('notifications-center.unknown-date')))
    .join(' – ');
};

const formatAddressValue = (value) =>
  value.raw ||
  [value.streetAddress, value.countryName, value.locality, value.postalCode, value.region].filter(Boolean).join(', ') ||
  null;

/** Display string for a scalar change value; null when there is nothing to show. */
export const formatChangeValue = (change, value) => {
  if (value === null || value === undefined || value === '') return null;

  if (MONEY_FIELDS.includes(change.field)) {
    return value.amount != null ? formatNumToCurrency(value.amount, 0) : null;
  }
  if (change.field === 'tenderPeriod') return formatTenderPeriod(value);
  if (DATE_FIELDS.includes(change.field)) return formatDateValue(value);
  if (change.field === 'deliveryAddress') return formatAddressValue(value);
  if (change.field === 'description') return truncate(String(value), DESCRIPTION_MAX_LENGTH);
  if (change.valueEnum) {
    const key = `notifications-center.enum.${change.field}.${value}`;
    return i18n.te(key) ? i18n.t(key) : String(value);
  }
  return String(value);
};

/** Old/new display values of a scalar change; description shows its new value only. */
export const getScalarValues = (change) => ({
  oldValue: change.field === 'description' ? null : formatChangeValue(change, change.oldValue),
  newValue: formatChangeValue(change, change.newValue),
});

/** Company names across all typed-list rows, for followed-competitor matching. */
export const collectCompanyNames = (tenderChanges) =>
  (tenderChanges || [])
    .filter((change) => TYPED_LIST_FIELDS.includes(change.field))
    .flatMap((change) => change.valuesChange || [])
    .flatMap(({ value }) => {
      const item = value || {};
      if (item.organization) return [item.organization.name];
      if (item.tenderer) return [item.tenderer.name];
      if (item.suppliers) return item.suppliers.map((supplier) => supplier.name);
      return [];
    })
    .filter(Boolean);

/** First company name of the changes matching a followed competitor; null when none match. */
export const getMatchedCompetitorName = (tenderChanges, competitors) =>
  collectCompanyNames(tenderChanges).find((name) =>
    (competitors || []).some((competitor) =>
      competitor.competitorNames.some(
        (competitorName) => normalizeStr(competitorName.normalizedName) === normalizeStr(name),
      ),
    ),
  ) || null;

const getThreadCompetitorName = (notification, competitors) => {
  if (notification.content.eventType !== NotificationSlugs.FOLLOWED_COMPETITOR_TENDER_UPDATED) return null;
  const payload = notification.content.payload;
  return getMatchedCompetitorName(payload && payload.tenderChanges, competitors);
};

/** Day buckets for the notifications center, in display order. */
export const NOTIFICATION_DAY_GROUPS = ['today', 'yesterday', 'this-week', 'this-month', 'older'];

// Parses with plain moment (not toMoment, whose string branch only handles calendar-date formats)
// because notification timestamps are full ISO datetimes.
export const getNotificationDayGroup = (date) => {
  const m = moment(date);
  if (!m.isValid()) return 'older';

  const now = moment();
  if (m.isSame(now, 'day')) return 'today';
  if (m.isSame(now.clone().subtract(1, 'day'), 'day')) return 'yesterday';
  // start of week is Monday
  if (m.isSame(now, 'isoWeek')) return 'this-week';
  if (m.isSame(now, 'month')) return 'this-month';
  return 'older';
};

const buildGroupEntries = (notifications, competitors) =>
  notifications.reduce((entries, notification) => {
    const competitorName = getThreadCompetitorName(notification, competitors);
    const lastEntry = entries[entries.length - 1];

    if (!competitorName) {
      entries.push({ type: 'notification', notification });
    } else if (lastEntry && lastEntry.type === 'thread' && lastEntry.competitorName === competitorName) {
      lastEntry.notifications.push(notification);
    } else {
      entries.push({ type: 'thread', competitorName, notifications: [notification] });
    }
    return entries;
  }, []);

/**
 * Panel render model: ordered day groups of entries. Consecutive followed-competitor
 * notifications matching the same competitor collapse into a `thread` entry; competitor
 * notifications without a match degrade to plain `notification` entries.
 */
export const groupNotificationEvents = (events, competitors) => {
  const groups = (events || []).reduce((acc, notification) => {
    const key = getNotificationDayGroup(notification.createdAt);
    (acc[key] = acc[key] || []).push(notification);
    return acc;
  }, {});

  return NOTIFICATION_DAY_GROUPS.filter((key) => groups[key]).map((key) => ({
    key,
    entries: buildGroupEntries(groups[key], competitors),
  }));
};
